import React from 'react';
import { Radio, RadioGroup, Typography, useTheme } from '@mui/joy';
import Box from '@mui/joy/Box';
import FormLabel from '@mui/joy/FormLabel';
import Sheet from '@mui/joy/Sheet';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

const Question = ({ questionContents, selectedAnswer, save, displayCorrect }) => {
    const theme = useTheme();
    const answerData = questionContents.answers;

    const correctAnswers = Array.isArray(questionContents.correct)
        ? questionContents.correct
        : [questionContents.correct];

    const isCorrect = selectedAnswer && correctAnswers.includes(selectedAnswer);

    return (
        <Box>
            {questionContents.imageUrl && (
                <img src={questionContents.imageUrl} alt={questionContents.imageUrl} />
            )}

            <FormLabel
                sx={{
                    mb: 2,
                    fontWeight: 'xl',
                    textTransform: 'uppercase',
                    fontSize: 'sm',
                    letterSpacing: '0.15rem',
                }}
            >
                {questionContents.question}
            </FormLabel>

            {questionContents.description && (
                <FormLabel
                    sx={{
                        mb: 2,
                        fontWeight: 'xl',
                        textTransform: 'uppercase',
                        fontSize: 'sm',
                        letterSpacing: '0.15rem',
                    }}
                >
                    <Typography fontSize="x-small">
                        {questionContents.description}
                    </Typography>
                </FormLabel>
            )}

            {displayCorrect && (
                <FormLabel
                    sx={{
                        mb: 2,
                        fontWeight: 'xl',
                        textTransform: 'uppercase',
                        fontSize: 'sm',
                        letterSpacing: '0.15rem',
                        color: isCorrect ? theme.vars.palette.success[500] : theme.vars.palette.danger[500],
                    }}
                >
                    {isCorrect ? 'Correct!' : 'Incorrect!'}
                    {questionContents.justification && (
                        <Typography fontSize="x-small">{questionContents.justification}</Typography>
                    )}
                </FormLabel>
            )}

            <RadioGroup
                aria-labelledby="storage-label"
                size="lg"
                sx={{ gap: 1.5 }}
                onChange={(e) => save(e.target.value)}
                value={selectedAnswer !== undefined ? selectedAnswer : ''}
                disabled={displayCorrect}
            >
                {Object.entries(answerData).map(([key, value]) => {
                    const isSelected = key === selectedAnswer;
                    const isCorrectAnswer = correctAnswers.includes(key);

                    const color = displayCorrect
                        ? isCorrectAnswer
                            ? theme.vars.palette.success[500]
                            : isSelected
                                ? theme.vars.palette.danger[500]
                                : theme.vars.palette.text.secondary
                        : isSelected
                            ? theme.vars.palette.primary[500]
                            : theme.vars.palette.text.secondary;

                    const borderColor = displayCorrect
                        ? isCorrectAnswer
                            ? theme.vars.palette.success[500]
                            : isSelected
                                ? theme.vars.palette.danger[500]
                                : theme.vars.palette.neutral.outlinedBorder
                        : isSelected
                            ? theme.vars.palette.primary[500]
                            : theme.vars.palette.neutral.outlinedBorder;

                    const icon =
                        displayCorrect && isCorrectAnswer ? (
                            <CheckIcon />
                        ) : displayCorrect && isSelected ? (
                            <CloseIcon />
                        ) : null;

                    return (
                        <Sheet
                            key={key}
                            sx={{ p: 2, borderRadius: 'md', boxShadow: 'sm', display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            {icon}
                            <Radio
                                label={value.text}
                                overlay
                                disableIcon
                                value={key}
                                slotProps={{
                                    label: () => ({
                                        sx: {
                                            fontWeight: 'lg',
                                            fontSize: 'md',
                                            color: color,
                                        },
                                    }),
                                    action: () => ({
                                        sx: {
                                            borderWidth: '1px',
                                            borderStyle: 'solid',
                                            borderColor: borderColor,
                                            borderRadius: 'md',
                                            pointerEvents: displayCorrect ? 'none' : 'auto',
                                            '&:hover': !displayCorrect && {
                                                backgroundColor: theme.vars.palette.neutral.softBg,
                                            },
                                        },
                                    }),
                                }}
                            />

                        </Sheet>
                    );
                })}
            </RadioGroup>
        </Box>
    );
};

export default Question;
